import { useState, type ReactElement } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';

import type { PageSeo } from '../Pages/hooks/useGetPageDetails';
import { SeoFields } from '../Pages/SeoSettings/SeoFields';

const EMPTY_SEO: PageSeo = {
  title: '',
  description: '',
  socialTitle: '',
  socialDescription: '',
  socialImageUrl: '',
};

interface AddPageModalProps {
  open: boolean;
  pageName: string;
  organisationId: string;
  isSaving: boolean;
  onClose: () => void;
  onCreate: (seo: PageSeo) => void;
}

export function AddPageModal({
  open,
  pageName,
  organisationId,
  isSaving,
  onClose,
  onCreate,
}: AddPageModalProps): ReactElement {
  // Mounted only while open (see callers), so the draft starts empty on every
  // open cycle without synchronising state from props.
  const [seo, setSeo] = useState<PageSeo>(EMPTY_SEO);

  const handleCreate = () => {
    // Blank fields are omitted rather than posted as "". The social image is optional, and an
    // empty string is not a URL — sending one made every page without a social image fail to
    // create. An absent key is how the API is already told a field has no value.
    const filled = Object.entries(seo).filter(([, value]) => value?.trim());
    // Safe: the keys come straight off PageSeo, filtering only drops them.
    onCreate(Object.fromEntries(filled) as PageSeo);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className="vtwo:flex vtwo:items-center vtwo:justify-between vtwo:font-bold">
        {`Add ${pageName} page`}
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <SeoFields
          value={seo}
          organisationId={organisationId}
          onChange={(field, value) => setSeo((prev) => ({ ...prev, [field]: value }))}
          titleRequired
        />
      </DialogContent>
      <DialogActions className="vtwo:px-6 vtwo:pb-6 vtwo:gap-3">
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isSaving}
          className="vtwo:flex-1 vtwo:rounded-lg vtwo:text-typography-primary vtwo:border-plus-border"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={isSaving || !seo.title?.trim()}
          className="vtwo:flex-1 vtwo:rounded-lg vtwo:bg-[#327D5D] vtwo:text-white vtwo:hover:bg-[#28644A]"
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
